import { AudioManager } from "../../AudioManager";
import { GameConstants } from "../../GameConstants";
import { GameManager } from "../../GameManager";
import { GameVars } from "../../GameVars";
import { Language } from "../../Types";
import { Button } from "../../utils/Button";
import { AudioButtonContainer } from "../AudioButtonContainer";
import { LanguageSelectionMenu } from "./LanguageSelectionMenu";
import { SplashScene } from "./SplashScene";

export class SettingsLayer extends Phaser.GameObjects.Container {

    private boundingRectangle: Phaser.Geom.Rectangle;
    private canBeClosed: boolean;
    private soundLabel: Phaser.GameObjects.Text;
    private languageSelectionMenu: LanguageSelectionMenu;

    constructor(scene: Phaser.Scene) {
        
        super(scene);

        this.canBeClosed = false;

        const lan = GameVars.gameData.language;
        const text = GameVars.currentScene.cache.json.get("text");

        const darkLayer = new Phaser.GameObjects.Graphics(this.scene);
        darkLayer.fillStyle(0x000000, 0.7);
        darkLayer.fillRect(0, 0, GameConstants.GAME_WIDTH, GameConstants.GAME_HEIGHT);
        this.add(darkLayer);

        const w = 405;
        const h = 450;

        this.boundingRectangle = new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h);

        const scaledItemsContainer = new Phaser.GameObjects.Container(this.scene);
        scaledItemsContainer.x = GameConstants.GAME_WIDTH / 2;
        scaledItemsContainer.y = GameConstants.GAME_HEIGHT / 2;
        scaledItemsContainer.scaleY = GameVars.scaleY;
        this.add(scaledItemsContainer);

        // EL SONIDO
        const soundBackground = new Phaser.GameObjects.Image(this.scene, 0, -159, "texture_atlas_1", "card_description_background");
        scaledItemsContainer.add(soundBackground);

        this.soundLabel = new Phaser.GameObjects.Text(scene, 70, soundBackground.y, text.SOUND_ON[lan], {fontFamily: "AlmendraBold", color: "#663227", fontSize: "40px", wordWrap: { width: 260, useAdvancedWrap: true }});
        this.soundLabel.setOrigin(1, .5);
        scaledItemsContainer.add(this.soundLabel);

        const audioButtonContainer = new AudioButtonContainer(this.scene, this.onSoundButtonClick, this);
        audioButtonContainer.x = 130;
        audioButtonContainer.y = soundBackground.y;
        scaledItemsContainer.add(audioButtonContainer);

        // EL TUTORIAL
        const tutorialBackground = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1", "card_description_background");
        scaledItemsContainer.add(tutorialBackground);

        const tutorialLabel = new Phaser.GameObjects.Text(scene, this.soundLabel.x, tutorialBackground.y, text.TUTORIAL[lan], {fontFamily: "AlmendraBold", color: "#663227", fontSize: "40px"});
        tutorialLabel.setOrigin(1, .5);
        scaledItemsContainer.add(tutorialLabel);

        const tutorialButton = new Button(this.scene, audioButtonContainer.x, tutorialBackground.y, "texture_atlas_1", "btn_tutorial");
        tutorialButton.setInflationTween(1.1);
        tutorialButton.onDown(this.onTutorial, this);
        scaledItemsContainer.add(tutorialButton);

        const languageBackground = new Phaser.GameObjects.Image(this.scene, 0, 150, "texture_atlas_1", "card_description_background");
        scaledItemsContainer.add(languageBackground);

        const languageLabel = new Phaser.GameObjects.Text(scene, this.soundLabel.x, languageBackground.y, this.getCurrentLanguage(), {fontFamily: "AlmendraBold", color: "#663227", fontSize: "40px"});
        languageLabel.setOrigin(1, .5);
        scaledItemsContainer.add(languageLabel);

        if (languageLabel.width > 250) {
            languageLabel.setScale(250 / languageLabel.width);
        }

        const languageButton = new Button(this.scene, audioButtonContainer.x, languageBackground.y, "texture_atlas_1", "btn_language");
        languageButton.setInflationTween(1.1);
        languageButton.onDown(this.onClickLanguage, this);
        scaledItemsContainer.add(languageButton);

        this.languageSelectionMenu = new LanguageSelectionMenu(this.scene);
        this.languageSelectionMenu.y = 200;
        this.languageSelectionMenu.visible = false;
        scaledItemsContainer.addAt(this.languageSelectionMenu, 0);

        if (GameConstants.DEBUG) {
            const debugGraphics = new Phaser.GameObjects.Graphics(this.scene);
            debugGraphics.lineStyle(2, 0x00ff00);
            debugGraphics.strokeRectShape(this.boundingRectangle);
            scaledItemsContainer.add(debugGraphics);
        }

        this.scene.input.on("pointerdown", this.onPointerDown, this);

        this.scene.time.delayedCall(150, () => this.canBeClosed = true, [], this);
    }

    private onPointerDown(pointer: Phaser.Input.Pointer): void {

        if (!this.canBeClosed || !this.scene) {
            return;
        }

        let x = this.scene.input.activePointer.x;
        let y = this.scene.input.activePointer.y;

        x -= GameConstants.GAME_WIDTH / 2;
        y -= GameConstants.GAME_HEIGHT / 2;

        y /= GameVars.scaleY;

        if (!this.boundingRectangle.contains(x, y)) {
            SplashScene.currentInstance.hideSettingsLayer();
        }
    }

    private onSoundButtonClick(): void {

        const lan = GameVars.gameData.language;
        const text = GameVars.currentScene.cache.json.get("text");

        if (GameVars.gameData.muted) {
            this.soundLabel.text = text.SOUND_OFF[lan];
        } else {
            this.soundLabel.text = text.SOUND_ON[lan];
        }
    }

    private onTutorial(): void {
        
        GameManager.enterBoardScene(true);

        AudioManager.playSound("btn_click");
    }

    private onExit(): void {
        
        GameManager.enterSplashScene();

        AudioManager.playSound("btn_click");
    }

    private onClickLanguage(): void {
        
        this.languageSelectionMenu.visible = true;
    }

    private getCurrentLanguage(): string {

        switch (GameVars.gameData.language) {

            case Language.EN:
                return "ENGLISH";
            case Language.ES:
                return "ESPAÑOL";
            case Language.DE:
                return "DEUTSCH";
            case Language.FR:
                return "FRANÇAIS";
            case Language.IT:
                return "ITALIANO";
            case Language.PT:
                return "PORTUGUÊS - BR";
            case Language.NL:
                return "NEDERLANDS";
            default:
                return "ENGLISH";
        }
    }
}
